import { Bar } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import { Checkbox, FormControlLabel } from '@mui/material';
import { useState } from 'react';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
);

const IULFeesChart = ({ chartData }: any) => {

    const [showTypicalPlan, setShowTypicalPlan] = useState(true);
    const [showIULFees, setShowIULFees] = useState(true);

    // Validate chart data
    const isValidChartData = chartData?.labels && chartData?.datasets && Array.isArray(chartData.datasets);

    const data = {
        labels: chartData?.labels,
        datasets: [
            ...(showTypicalPlan ? [{
                label: "Typical Plan Fees",
                data: chartData?.datasets?.[1]?.data,
                backgroundColor: "rgba(243, 184, 170, 1)",
                borderColor: "rgba(243, 184, 170, 1)",
            }] : []),
            ...(showIULFees ? [{
                label: "IUL Fees",
                data: chartData?.datasets?.[0]?.data,
                backgroundColor: "rgba(245, 206, 164, 1)",
                borderColor: "rgba(245, 206, 164, 1)",
            }] : [])
        ]
    };

    const options: any = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: true,
                position: 'top' as const,
                align: 'end' as const,
                labels: {
                    color: '#6B7280',
                    font: {
                        size: 12,
                        weight: "500",
                    },
                    usePointStyle: true,
                    pointStyle: "circle" as const,
                    boxWidth: 8,
                    boxHeight: 8,
                    generateLabels: function (chart: any) {
                        const datasets = chart.data.datasets;
                        return datasets.map((dataset: any, i: number) => ({
                            text: dataset.label,
                            fillStyle: dataset.backgroundColor,
                            strokeStyle: dataset.backgroundColor,
                            lineWidth: 0,
                            hidden: false,
                            index: i,
                        }));
                    }
                }
            },
            title: {
                display: false,
            },
            tooltip: {
                callbacks: {
                    label: (context: any) => {
                        const value = context.parsed.y;
                        return `${context.dataset.label}: $${value.toLocaleString()}`;
                    },
                    titleFont: { size: 18 },
                    bodyFont: { size: 16 },
                }
            }
        },
        scales: {
            x: {
                grid: {
                    display: false
                },
                ticks: {
                    color: '#6B7280',
                    font: {
                        size: 12,
                    }
                },
                border: { display: false }
            },
            y: {
                grid: {
                    color: 'rgba(148, 163, 184, 0.4)',
                    borderDash: [4, 4],
                },
                border: { display: false },
                ticks: {
                    color: '#6B7280',
                    font: {
                        size: 12,
                    },
                    callback: function (value: any) {
                        return '$' + value?.toLocaleString();
                    }
                }
            },
        },
        elements: {
            bar: {
                borderRadius: 0,
                borderSkipped: false,
            }
        },
        barPercentage: 1,
        categoryPercentage: 0.7,
    };

    return (
        <div style={{
            width: '100%',
            backgroundColor: '#f1f5f9',
            border: '1px solid #e2e8f0',
            padding: '12px 16px',
            borderRadius: '6px',
            overflow: "auto",
            marginBottom: '20px',
        }}>
            <div style={{
                display: 'flex',
                gap: '20px',
                alignItems: 'center',
                marginBottom: '10px',
                justifyContent: "center"
            }}>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={showTypicalPlan}
                            onChange={(e) => setShowTypicalPlan(e.target.checked)}
                            sx={{
                                '&.Mui-checked': {
                                    color: '#3B78CE',
                                },
                                '& .MuiSvgIcon-root': {
                                    fontSize: '21px',
                                },
                                cursor: 'pointer',
                                color: '#3B78CE',
                            }}
                        />
                    }
                    label="Typical Plan Fees"
                    sx={{
                        color: '#6B7280',
                        cursor: 'pointer',
                        margin: 0,
                        '& .MuiFormControlLabel-label': {
                            fontSize: '13px',
                            fontWeight: '600',
                        }
                    }}
                />
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={showIULFees}
                            onChange={(e) => setShowIULFees(e.target.checked)}
                            sx={{
                                '&.Mui-checked': {
                                    color: '#3B78CE',
                                },
                                '& .MuiSvgIcon-root': {
                                    fontSize: '21px',
                                },
                                cursor: 'pointer',
                                color: '#3B78CE',
                            }}
                        />
                    }
                    label="IUL Fees"
                    sx={{
                        color: '#6B7280',
                        cursor: 'pointer',
                        margin: 0,
                        '& .MuiFormControlLabel-label': {
                            fontSize: '13px',
                            fontWeight: '600',
                        }
                    }}
                />
            </div>
            {isValidChartData && (showTypicalPlan || showIULFees) ? (
                <div style={{ minWidth: '1000px', height: "630px" }}>
                    <Bar data={data} options={options} />
                </div>
            ) : (
                <div style={{
                    color: '#475569',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '630px',
                    fontSize: '16px'
                }}>
                    No chart data available.
                </div>
            )}
        </div>
    );
};

export default IULFeesChart;
